import { Controller, useForm } from 'react-hook-form';
import React, { useContext, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useModal } from 'store/modals';
import { Button } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { ShouldRender } from 'components/ShouldRender';
import { Grid } from 'components/layout/Grid';
import { Brikk } from 'components/Brikk';
import { Text } from 'components/typography';
import { MultiSelect } from 'components/form-elements/selects/MultiSelect';
import { useUpdateZone, useGetDevices, useGetZoneGroups, useGetZones } from 'services/2n/zones';
import { LabeledElement } from 'components/form-elements/labels/LabeledElement';
import { PageLoader } from 'components/loaders/PageLoader';
import { DeleteResource } from '../DeleteResource';
import styles from '../../../styles.module.scss';
import { AccessManagement2NModalContext } from '../../../AccessManagement2NModalContext';

export const ResourceForm = () => {

  const intl = useIntl();
  const zones = useGetZones();
  const { selectedResource, setSelectedResource } = useContext(AccessManagement2NModalContext);
  const { openModal } = useModal();

  const allDevices = useGetDevices();
  const devicesOptions = useMemo(
    () => (allDevices.devices || []).map((device) => ({ value: device.Id, label: device.Name })),
    [allDevices.devices],
  );
  const assignedDevicesOptions = useMemo(
    () => ((selectedResource && selectedResource.devices) || [])
      .map((device) => ({ value: device.id, label: device.name })),
    [selectedResource],
  );
  const zoneGroups = useGetZoneGroups(selectedResource && selectedResource.id);

  const formattedZoneGroups = zoneGroups.groups.length > 0 ? zoneGroups.groups.map((group) => ({
    value: group.Id,
    label: group.Name,
  })) : [];

  const { control, handleSubmit, resetField } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      resourceName: (selectedResource && selectedResource.name) || '',
      resourceDescription: (selectedResource && selectedResource.description) || '',
      devicesInThisResource: assignedDevicesOptions,
    },
  });

  const { mutateAsync: updateZone, isLoading: isUpdating } = useUpdateZone();

  const onSubmit = async (data) => {
    const id = selectedResource && (selectedResource.id || selectedResource.Id);
    await updateZone({
      id,
      zone: {
        name: data.resourceName,
        description: data.resourceDescription,
        deviceIds: data.devicesInThisResource ? data.devicesInThisResource.map((d) => d.value) : [],
      },
    });
  };

  useEffect(() => {
    resetField('devicesInThisResource', { defaultValue: assignedDevicesOptions });
  }, [assignedDevicesOptions]);

  useEffect(() => {
    resetField('resourceName', { defaultValue: (selectedResource && selectedResource.name) || '' });
    resetField('resourceDescription', { defaultValue: (selectedResource && selectedResource.description) || '' });
  }, [selectedResource]);

  if (zones.isLoading) return <div className={styles.container}><PageLoader /></div>;

  return (
    <div className={styles.container}>
      <Brikk.Heading
        heading="editResource"
      />
      <ShouldRender condition={selectedResource && Object.hasOwn(selectedResource, 'name')}>
        <Text
          element="h2"
          thin
          xl
          size={20}
        >
          {String((selectedResource && selectedResource.name))}
        </Text>
      </ShouldRender>
      {selectedResource ? (
        <Grid
          noPadding
          oneColumn
        >
          <Form
            className={styles.formContainer}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className={styles.formWrapper}>
              <Grid
                noPadding
                oneColumn
              >
                <div className={styles.form}>
                  <Controller
                    name="resourceName"
                    control={control}
                    render={({ field: { ref, onChange, value } }) => (
                      <LabeledInput
                        labelIntl="resourceName"
                        id="name"
                        value={value || ''}
                        inputRef={ref}
                        onChange={onChange}
                      />
                    )}
                  />

                  <Controller
                    name="devicesInThisResource"
                    control={control}
                    render={({ field }) => {
                      const handleChange = (newValue) => {
                        const oldValue = field.value || [];
                        const newArr = newValue || [];

                        const removed = oldValue.filter(oldItem => !newArr.some(n => n.value === oldItem.value));
                        if (removed.length > 0) {

                          openModal({
                            modalType: 'ConfirmationModal',
                            modalProps: {
                              title: intl.formatMessage({ id: 'deleteDeviceConfirm' }),
                              content: intl.formatMessage({ id: 'deleteDeviceConfirmContent' }),
                              onConfirm: () => field.onChange(newValue),
                            },
                          });
                        } else {
                          field.onChange(newValue);
                        }
                      };

                      return (
                        <MultiSelect
                          name="devicesInThisResource"
                          options={devicesOptions}
                          title={intl.formatMessage({ id: 'devicesInThisResource' })}
                          value={field.value}
                          onChange={handleChange}
                        />
                      );
                    }}
                  />

                  {formattedZoneGroups.length > 0 ? (
                    <MultiSelect
                      options={formattedZoneGroups}
                      title={intl.formatMessage(
                        { id: 'resourceInGroups' },
                        { count: formattedZoneGroups.length },
                      )}
                      value={formattedZoneGroups}
                      isClearable={false}
                      isSearchable
                      isLoading={zoneGroups.isLoading}
                    />
                  ) : (
                    <LabeledElement label={intl.formatMessage(
                      { id: 'resourceInGroups' },
                      { count: formattedZoneGroups.length },
                    )}
                    />
                  )}
                </div>
              </Grid>
            </div>
            <div className={styles.formButtons}>
              <Button
                basic
                name="cancel-resource-details"
                onClick={() => setSelectedResource(null)}
              >
                <FormattedMessage id="cancel" />
              </Button>
              <Button
                type="submit"
                name="submit-resource-details"
                loading={isUpdating}
              >
                <FormattedMessage id="saveChanges" />
              </Button>
            </div>
          </Form>

          <ShouldRender condition={selectedResource}>
            <DeleteResource />
          </ShouldRender>

        </Grid>
      ) : (
        <Text
          secondary
          center
          intl="noResourceSelected"
          size={16}
        />
      )}
    </div>
  );
};
